import type { PopupConfig } from '@publishing/components/popup/types';
import { PopupContent } from '@publishing/components/popup/PopupContent';
import { Button, ButtonGroup } from '@publishing/components/ui/Button';

import officeInteriorPopupImg from '@publishing/assets/images/popup/popup-interior-webinar-261020.jpg';
import holidayPopupImg from '@publishing/assets/images/popup/popup-holiday-260908.jpg';
import s from '@publishing/components/popup/CommonPopup.module.scss';
import Link from 'next/link';
import { Picture } from '@/publishing/components/ui/Picture';

/**
 * 활성 팝업 목록 -- 팝업 추가/제거 시 이 배열만 수정
 *
 * @example 이미지 + 링크 (특정 페이지에만 노출)
 * {
 *   id: 'event1',
 *   paths: ['/'],
 *   content: (
 *     <PopupContent>
 *      <Link href="/event/summer">
 *        <Picture src="/popup/event.jpg" mobileSrc="/popup/event_mo.jpg" alt="이벤트" href="/event/summer" />
 *      </Link>
 *     </PopupContent>
 *   ),
 * }
 *
 * @example 특정 페이지 제외 전체 노출 + 검은색 닫기 버튼
 * {
 *   id: 'notice1',
 *   excludePaths: ['/for-enterprise'],
 *   content: (
 *     <PopupContent>
 *       <Picture src="/popup/notice.jpg" alt="공지" />
 *       <ButtonGroup align="center" className={s.button_group}>
 *        <Button href="/event/detail" className={s.button}>자세히 보기</Button>
 *       </ButtonGroup>
 *     </PopupContent>
 *   ),
 * }
 *
 * @example CTA / 닫기 스타일. type 기본 black, outline은 채움 그대로 + 보더
 * 1. CTA white, 닫기 white
 *    <PopupContent close={{ type: 'white' }}><Button type="white" className={s.button}>...</Button></PopupContent>
 * 
 * 2. CTA black, 닫기 black
 *    <PopupContent close={{ type: 'black' }}><Button type="black" className={s.button}>...</Button></PopupContent>
 * 
 * 3. CTA white, 닫기 black
 *    <PopupContent close={{ type: 'black' }}><Button type="white" className={s.button}>...</Button></PopupContent>
 * 
 * 4. CTA black, 닫기 white
 *    <PopupContent close={{ type: 'white' }}><Button type="black" className={s.button}>...</Button></PopupContent>
 * 
 * 5. CTA outline white - 흰 배경, 텍스트 검정, 보더 gray-400
 *    <Button type="white" outline className={s.button}>...</Button>
 * 
 * 6. CTA outline black - 검정 배경, 텍스트 흰색, 보더 gray-600
 *    <Button type="black" outline className={s.button}>...</Button>
 *
 * @example 게시 기간 설정 (날짜만 또는 시간 포함)
 * {
 *   id: 'summerEvent',
 *   startDate: '2026-06-10',          // 6/10 00:00부터
 *   endDate: '2026-06-30 18:00',      // 6/30 18:00까지
 *   // startDate: '2026-06-10 9:30',  // 월·일·시·분 자릿수 무관, 분 생략 가능 ('2026-6-10 9' 도 OK)
 *   content: ( ... ),
 * }
 *
 * @example PC 노출 위치 — 화면 가장자리로부터의 거리(px).
 * pos를 주면 딤드 없이(스크롤 가능) 그 위치에 붙고, 미설정 시 딤드 + 중앙. 모바일은 항상 딤드 + 중앙
 * {
 *   pos: { top: 160, left: 160 },     // top/bottom은 160 또는 105
 *   // pos: { top: 105, right: 48 },  // left/right는 160 또는 48
 * }
 *
 * ButtonGroup, Button에 적용된 s.button_group, s.button 클래스는 팝업 기본 버튼 스타일.
 * 커스텀이 필요하면 해당 클래스를 삭제하고 자유롭게 스타일링하면 됨.
 */

export const POPUP_LIST: PopupConfig[] = [
  {
    id: 'officeInteriorTco',
    paths: ['/office-interior'],
    startDate: '2026-10-16',
    endDate: '2026-11-05 12:00',
    pos: { top: 160, right: 160 },
    content: (
      <PopupContent>
        <Picture src={officeInteriorPopupImg} alt='사무실 이전, 인테리어 말고도 챙겨야할 7가지 비용' />
        <ButtonGroup align='center' className={s.button_group}>
          <Button className={s.button} type="white" target="_blank" href="https://docurise.io/marketing/d/2611webinar?src=newff_popup">무료 웨비나 신청</Button>
        </ButtonGroup>
      </PopupContent>
    ),
  },
  {
    id: 'holidayTour',
    paths: ['/','/office-space','/shared-office','/location/co-work','/custom-office','/private-office'],
    startDate: '2026-09-21',
    endDate: '2026-09-27',
    pos: { top: 160, right: 160 },
    content: (
      <PopupContent close={{ type: 'white' }}> 
        <Picture src={holidayPopupImg} alt='공유오피스 추석 투어' />
        <ButtonGroup align='center' className={s.button_group}>
          <Button className={s.button} type="white" outline target="_blank" href="/contact-holiday">무료 투어 예약하기</Button>
        </ButtonGroup>
      </PopupContent>
    ),
  },
  
];

/**
 * 정기주주총회 소집 공고
 * 매년 동일 형식 — 제목, 본문, 버튼 텍스트/링크, 게시 기간만 수정
 */
const SHAREHOLDERS_MEETING: PopupConfig = {
  id: 'shareholdersMeeting',
  paths: ['/'],
  startDate: '2026-10-28',
  endDate: '2026-10-30',
  autoCloseMs: 3000,
  pos: { top: 160, left: 48 },
  content: (
    <PopupContent className={s.shareholders_meeting}>
      {/* 텍스트가 넘쳐서 임시로 텍스트 크기 줄임. 스타일 body-700 -> 600으로 임시 변경 */}
      <h3 className={s.shareholders_meeting_title}>무상증자 단주처리로 취득한 자기주식 소각공고</h3>
      {/* <h3 className={s.shareholders_meeting_title}>제00기 정기주주총회 소집 공고</h3>
      <p className={s.shareholders_meeting_text}>
        상법 제363조 및 당사 정관 제00조에 의거하여
        제00기 정기주주총회를 아래와 같이 소집하오니
        참석하여 주시기 바랍니다.
      </p> */}
      <ButtonGroup align="center">
        <Button 
          size="small"
          className={s.button}
          target='_blank'
          href="https://corp.fastfive.co.kr/archives/954"
        >공고 보기</Button>
      </ButtonGroup>
    </PopupContent>
  ),
};

POPUP_LIST.push(SHAREHOLDERS_MEETING);
